import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./StockMovementHistoryPanel.css";

function todayKey() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatQty(value) {
  return Number(value || 0).toLocaleString();
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

const MOVEMENT_TYPES = [
  "ALL",
  "IMPORT_RECEIVE",
  "TRANSFER_IN",
  "TRANSFER_OUT",
  "POS_SALE",
  "SALE_RETURN",
  "ADJUSTMENT_IN",
  "ADJUSTMENT_OUT",
  "DAMAGE",
  "LOST",
];

export default function StockMovementHistoryPanel() {
  const [location, setLocation] = useState("ALL");
  const [movementType, setMovementType] = useState("ALL");
  const [productCode, setProductCode] = useState("");
  const [dateFrom, setDateFrom] = useState(todayKey());
  const [dateTo, setDateTo] = useState(todayKey());

  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadMovements() {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();

      query.set("location", location);
      query.set("limit", "500");

      if (movementType !== "ALL") {
        query.set("movementType", movementType);
      }

      if (productCode.trim()) {
        query.set("productCode", productCode.trim());
      }

      if (dateFrom) query.set("dateFrom", dateFrom);
      if (dateTo) query.set("dateTo", dateTo);

      const res = await fetch(`${API_BASE}/api/erp/stock-movements?${query.toString()}`);
      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load stock movements");
      }

      setRows(data.rows || []);
      setPage(1);
    } catch (err) {
      setError(err.message || "Stock movement history loading failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, movementType, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const showingFrom = rows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, rows.length);

  function goPrev() {
    setPage((p) => Math.max(1, p - 1));
  }

  function goNext() {
    setPage((p) => Math.min(totalPages, p + 1));
  }

  return (
    <div className="stockHistory">
      <div className="stockHistory__head">
        <div>
          <span>Movement Ledger</span>
          <h2>Stock Movement History</h2>
          <p>
            Data များလာရင် page by page ကြည့်မယ်။ Default အနေနဲ့ latest 500 rows ထဲက pagination ပြမယ်။
          </p>
        </div>

        <button onClick={loadMovements} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="stockHistoryError">{error}</div>}

      <div className="stockHistoryFilters">
        <label>
          <span>From Date</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>

        <label>
          <span>To Date</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>

        <label>
          <span>Location</span>
          <LocationSelect
            value={location}
            onChange={setLocation}
            mode="all"
            includeAll
            allLabel="All Locations"
          />
        </label>

        <label>
          <span>Movement Type</span>
          <select
            value={movementType}
            onChange={(e) => setMovementType(e.target.value)}
          >
            {MOVEMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type === "ALL" ? "All Types" : type}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Product Code</span>
          <input
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadMovements();
            }}
            placeholder="3423H"
          />
        </label>

        <button onClick={loadMovements} disabled={loading}>
          Search
        </button>
      </div>

      <div className="stockHistoryToolbar">
        <div>
          Showing <b>{showingFrom}</b> - <b>{showingTo}</b> of{" "}
          <b>{rows.length}</b> movements
        </div>

        <div className="stockHistoryPageControls">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={25}>25 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>

          <button onClick={goPrev} disabled={page <= 1}>
            Prev
          </button>

          <span>
            Page <b>{page}</b> / {totalPages}
          </span>

          <button onClick={goNext} disabled={page >= totalPages}>
            Next
          </button>
        </div>
      </div>

      <div className="stockHistoryTableWrap">
        <table className="stockHistoryTable">
          <thead>
            <tr>
              <th>Date / Time</th>
              <th>Code</th>
              <th>Color</th>
              <th>Size</th>
              <th>Location</th>
              <th>Type</th>
              <th>In / Out</th>
              <th>Qty</th>
              <th>Unit Cost</th>
              <th>Total Cost</th>
              <th>From</th>
              <th>To</th>
              <th>Voucher</th>
              <th>Batch</th>
              <th>Note</th>
            </tr>
          </thead>

          <tbody>
            {pagedRows.length === 0 ? (
              <tr>
                <td colSpan="15" className="stockHistoryEmpty">
                  No stock movement history.
                </td>
              </tr>
            ) : (
              pagedRows.map((row) => {
                const direction = String(row.direction || "").toUpperCase();

                return (
                  <tr key={row.id}>
                    <td>
                      <b>
                        {row.movement_time
                          ? new Date(row.movement_time).toLocaleString()
                          : row.movement_date}
                      </b>
                    </td>
                    <td>
                      <b>{row.product_code}</b>
                    </td>
                    <td>{row.color || "-"}</td>
                    <td>{row.size || "-"}</td>
                    <td>{row.location_code}</td>
                    <td>
                      <span className="stockHistoryType">{row.movement_type}</span>
                    </td>
                    <td>
                      <span
                        className={
                          direction === "IN"
                            ? "stockHistoryDirection stockHistoryDirection--in"
                            : "stockHistoryDirection stockHistoryDirection--out"
                        }
                      >
                        {direction}
                      </span>
                    </td>
                    <td>
                      <b>{formatQty(row.qty)}</b>
                    </td>
                    <td>{formatMoney(row.unit_cost_mmk)} Ks</td>
                    <td>{formatMoney(row.total_cost_mmk)} Ks</td>
                    <td>{row.from_location_code || "-"}</td>
                    <td>{row.to_location_code || "-"}</td>
                    <td>{row.voucher_no || "-"}</td>
                    <td>{row.batch_no || "-"}</td>
                    <td className="stockHistoryNote">{row.note || "-"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
